import {Tooltip} from 'components/Tooltip';
import React, {FC, useCallback, useContext, useEffect, useState} from 'react';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import style from './style.module.scss';
import {Typography} from 'components/Typography';
import classNames from 'classnames';
import Icon from 'components/Icon';
import {Button} from 'components/Button';
import Picture from 'components/Picture';
import {showModal} from 'modules/modals/thunks';
import {MODALS} from 'modules/modals/constants';
import {CollapseTextInline} from 'components/CollapseTextInline';
import {IExpressOrder} from 'plugins/modules/filter/types';
import {useDeleteExpressOrderMutation, useGetExpressOrderQuery} from 'modules/products/services';
import {FilterPickupTodayButtonContext} from 'plugins/modules/filter/context/FilterPickupTodayButtonContext';
import {InPlaceNotification} from 'components/InPlaceNotification';
import {WineryLocation} from 'modules/express-order/components/ExpressOrderPopup/WineryLocation';
import {WinerySchedule} from 'modules/express-order/components/ExpressOrderPopup/WinerySchedule';
import {useIsMobileByScreen} from 'core/hooks/useIsMobileByScreen';
import {MobileModal} from 'modules/modals/components/MobileModal';
import {useNextStep} from 'nextstepjs';
import {useExpressOrderGtag} from 'hooks/analytics/gtag/catalog/useExpressOrderGtag';

interface IProps {
    resolution: 'MOBILE' | 'DESKTOP';
}

// eslint-disable-next-line max-lines-per-function,complexity
export const ExpressOrderPopup: FC<IProps> = ({resolution}) => {
    const dispatch = useAppDispatch();
    const expressOrderResponse = useGetExpressOrderQuery();
    const isMobile = useIsMobileByScreen();
    const [wineShop, setWineShop] = useState<IExpressOrder>();
    const [isOpenTooltip, setOpenTooltip] = useState<boolean>(false);
    const [isOpenNotification, setIsOpenNotification] = useState<boolean>(false);
    const {handleResetPagination} = useContext(FilterPickupTodayButtonContext);
    const [isMobileModalOpen, setMobileModalOpen] = useState<boolean>(false);
    const {closeNextStep, isNextStepVisible} = useNextStep();

    const [deleteExpressOrder] = useDeleteExpressOrderMutation();
    const {
        sendDiscardEvent,
        sendClosePopupEvent,
        sendClickPopupEvent,
        sendShowPopupEvent,
        sendShowWineryPopupEvent,
        sendClickWineryPopupEvent,
    } = useExpressOrderGtag();

    const handleCloseTooltip = useCallback(() => {
        setOpenTooltip(false);
        setMobileModalOpen(false);
        closeNextStep();
        sendClosePopupEvent(Boolean(wineShop));
    }, [closeNextStep, sendClosePopupEvent, wineShop]);

    const handleClearExpressOrder = useCallback(async () => {
        try {
            const response = await deleteExpressOrder();
            if ('error' in response || !response.data?.ok) {
                return;
            }
            if (wineShop) {
                sendDiscardEvent(wineShop?.titleVinoteki);
            }
            handleResetPagination?.();
            setWineShop?.(undefined);
            handleCloseTooltip();
        } catch (e) {
            console.error(e);
        }
    }, [deleteExpressOrder, handleCloseTooltip, handleResetPagination, sendDiscardEvent, wineShop]);

    const handleChoose = useCallback(() => {
        sendClickWineryPopupEvent();
        dispatch(showModal(MODALS.EXPRESS_ORDER.name)).then(() => {
            setOpenTooltip(false);
            setIsOpenNotification(false);
            setMobileModalOpen(false);
            closeNextStep();
            sendClickPopupEvent(Boolean(wineShop));
            sendShowWineryPopupEvent('header');
        });
    }, [closeNextStep, dispatch, sendClickPopupEvent, sendClickWineryPopupEvent, sendShowWineryPopupEvent, wineShop]);

    useEffect(() => {
        let shop = expressOrderResponse?.data?.data;
        setWineShop(Array.isArray(shop) ? undefined : shop);
    }, [expressOrderResponse]);

    useEffect(() => {
        if (wineShop) {
            setIsOpenNotification(true);
            setTimeout(() => {
                setIsOpenNotification(false);
            }, 4_000);
        }
    }, [wineShop]);

    const overlayInitial = (
        <div className={style.overlayWrapper}>
            <Picture className={style.pictureBg} src={'/images/express-order/express-order-popup-bg.webp'} />
            <Icon className={style.pinIcon} icon={'map-pin-colored'} />
            <div className={style.content}>
                <Typography variant={'label-14-r'}>Заберите заказ через час из удобной винотеки</Typography>
                <Button onClick={handleChoose} className={style.chooseButton} size={'small'} theme={'primary'}>
                    Выбрать
                </Button>
                <Button onClick={handleCloseTooltip} size={'small'} theme={'ghost'} className={style.closeButton}>
                    <Icon className={style.locationIcon} icon={'close'} />
                </Button>
            </div>
        </div>
    );

    const overlayWinery = (
        <div className={style.overlayWrapperWinery}>
            <div className={style.contentWinery}>
                <Typography variant={'label-16-m'} className={style.wineryTitle}>
                    {wineShop?.titleVinoteki}
                </Typography>
                {wineShop && (
                    <WineryLocation
                        geoposition={wineShop.geoposition}
                        address={wineShop.address}
                        metroStations={wineShop.metroStations}
                    />
                )}
                {wineShop && <WinerySchedule schedule={wineShop?.typedSchedule} />}
                <div className={style.wineryActions} id={'EXPRESS_ORDER_ACTIONS'}>
                    <Button
                        className={style.wineryChangeButton}
                        textClassName={style.wineryChangeButtonText}
                        onClick={handleChoose}
                        theme={'secondary'}
                    >
                        Изменить
                    </Button>
                    <Button
                        onClick={handleClearExpressOrder}
                        className={style.wineryCloseButton}
                        size={'large'}
                        theme={'secondary'}
                    >
                        <Icon icon={'delete-new'} />
                    </Button>
                </div>
                <Button onClick={handleCloseTooltip} size={'small'} theme={'ghost'} className={style.closeButton}>
                    <Icon className={style.locationIcon} icon={'close'} />
                </Button>
            </div>
        </div>
    );

    const overlay = isMobile && !isNextStepVisible ? undefined : (wineShop && overlayWinery) || overlayInitial;

    const getNotificationVisibility = () => {
        if (!wineShop) {
            return false;
        }
        if ((isMobile && 'MOBILE' === resolution) || (!isMobile && 'DESKTOP' === resolution)) {
            return isOpenNotification;
        }

        return false;
    };

    const titleVinotekiExceptions = {
        ['7' as string]: ' & ресторана GRAND CRU',
        ['51' as string]: ' SimpleWine и бара SimpleBar Черемушкинский рынок',
    };

    return (
        <>
            <InPlaceNotification
                type={'check'}
                place={isMobile ? 'bottom' : 'bottom-start'}
                isVisible={getNotificationVisibility()}
                text={`В каталоге будут показаны товары из винотеки ${titleVinotekiExceptions[wineShop?.warehouseCode as string] ?? wineShop?.titleVinoteki}`}
            >
                <Tooltip
                    id={`EXPRESS_ORDER_TOOLTIP_${resolution}`}
                    isOpen={isMobile && !isNextStepVisible ? false : isOpenTooltip}
                    afterShow={() => {
                        setOpenTooltip(true);
                        sendShowPopupEvent(Boolean(wineShop));
                    }}
                    childrenClassName={style.locationContainer}
                    className={style.locationPopup}
                    openOnClick={true}
                    setIsOpen={(value) => {
                        setOpenTooltip(value);
                        if (!value) {
                            closeNextStep();
                            sendClosePopupEvent(Boolean(wineShop));
                        }
                    }}
                    overlay={overlay}
                >
                    <button
                        id={`EXPRESS_ORDER_TOOLTIP_${resolution}_BUTTON`}
                        onClick={() => {
                            if (isMobile && !wineShop) {
                                handleChoose();
                            } else if (isMobile && wineShop) {
                                setMobileModalOpen(true);
                                sendShowPopupEvent(Boolean(wineShop));
                            }
                        }}
                        className={classNames(style.expressOrderTooltipContainer)}
                    >
                        <Icon className={style.locationIcon} icon={'location-placemark-new'} />
                        <Typography
                            className={classNames(style.itemContainer)}
                            variants={{DT: 'label-14-r', TB: 'label-14-r', MB: 'label-12-r'}}
                            element={'span'}
                        >
                            {!wineShop ? (
                                'Выбрать винотеку'
                            ) : (
                                <CollapseTextInline
                                    hasExpandButton={false}
                                    text={wineShop.titleVinoteki}
                                    maxTextLength={36}
                                />
                            )}
                        </Typography>
                    </button>
                </Tooltip>
            </InPlaceNotification>
            {isMobileModalOpen && (
                <MobileModal
                    className={style.modalContent}
                    classNames={{closeButton: style.modalCloseButton}}
                    onClose={() => {
                        setMobileModalOpen(false);
                        closeNextStep();
                        sendClosePopupEvent(Boolean(wineShop));
                    }}
                >
                    {overlayWinery}
                </MobileModal>
            )}
        </>
    );
};
